import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/utils/api";

export default function WorkerDashboard() {
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const [progressRes, coursesRes] = await Promise.all([
        api.get("/progress/my-courses"),
        api.get("/courses"),
      ]);

      const progressMap = {};
      progressRes.data.forEach((p) => {
        progressMap[p.course_id] = p;
      });

      setProgress(progressMap);

      // Filter courses that have progress
      const assignedCourses = coursesRes.data.filter(
        (c) => progressMap[c.id]
      );
      setCourses(assignedCourses);
    } catch (error) {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (courseId) => {
    const prog = progress[courseId];
    if (!prog || !prog.module_progress.length) return 0;

    const totalModules = prog.module_progress.length;
    const completedModules = prog.module_progress.filter(
      (m) => m.quiz_passed
    ).length;

    return Math.round((completedModules / totalModules) * 100);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success("Logged out successfully");
    navigate("/worker/login");
  };

  const downloadCertificate = async (courseId) => {
    try {
      const response = await api.get(`/certificates/generate/${courseId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${courseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Certificate downloaded!");
    } catch (error) {
      toast.error("Failed to download certificate");
    }
  };

  if (loading) {
    return (
      <div className="worker-ui min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#2e7fd4] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your training...</p>
        </div>
      </div>
    );
  }

  const completedCourses = courses.filter(c => progress[c.id]?.completed);
  const inProgressCourses = courses.filter(c => !progress[c.id]?.completed);

  return (
    <div className="worker-ui min-h-screen bg-slate-50" data-testid="worker-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0a2240] to-[#0f325c] text-white px-6 py-6 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm mb-1">Welcome back,</p>
            <h1 className="text-2xl font-bold">{user.full_name}</h1>
          </div>
          <button
            onClick={() => navigate("/worker/profile")}
            className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            data-testid="profile-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </button>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{completedCourses.length}</p>
            <p className="text-xs text-white/70">Completed</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{inProgressCourses.length}</p>
            <p className="text-xs text-white/70">In Progress</p>
          </div>
        </div>
      </div>

      {/* Courses */}
      <div className="px-4 py-6 space-y-4">
        {courses.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Courses Yet</h3>
            <p className="text-slate-600">Your assigned courses will appear here</p>
          </div>
        ) : (
          courses.map((course) => {
            const prog = calculateProgress(course.id);
            const isCompleted = progress[course.id]?.completed;

            return (
              <div
                key={course.id}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all"
                data-testid="course-card"
              >
                {/* Certificate Ready Banner */}
                {isCompleted && (
                  <div className="bg-gradient-to-r from-[#0e9f8e] to-[#0b7d70] px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-white font-semibold text-sm">Certificate Ready!</span>
                    </div>
                    <button 
                      onClick={() => downloadCertificate(course.id)}
                      className="text-white text-sm font-bold flex items-center gap-1 hover:underline"
                      data-testid="quick-download-cert"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                  </div>
                )}

                {course.thumbnail_url && (
                  <img
                    src={course.thumbnail_url}
                    alt={course.title}
                    className="w-full h-40 object-cover"
                  />
                )}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {course.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {course.description}
                  </p>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600 font-medium">Progress</span>
                      <span className={`font-bold ${isCompleted ? 'text-[#0e9f8e]' : 'text-[#2e7fd4]'}`}>
                        {isCompleted ? 'Completed' : `${prog}%`}
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${isCompleted ? 100 : prog}%` }}
                      ></div>
                    </div>
                  </div>

                  {isCompleted ? (
                    <Button
                      onClick={() => navigate("/worker/certificates")}
                      className="w-full h-12 bg-[#0e9f8e] hover:bg-[#0b7d70] text-white font-semibold"
                      data-testid="view-certificate-button"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      VIEW CERTIFICATE
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate(`/worker/course/${course.id}`)}
                      className="w-full h-12 bg-[#0a2240] hover:bg-[#0f325c] text-white font-semibold"
                      data-testid="continue-course-button"
                    >
                      {prog > 0 ? "CONTINUE TRAINING" : "START TRAINING"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 py-2 px-4 flex justify-around max-w-[480px] mx-auto shadow-lg">
        <button
          onClick={() => navigate("/worker/dashboard")}
          className="flex flex-col items-center py-2 px-4 text-[#0a2240]"
          data-testid="nav-courses"
        >
          <svg className="w-6 h-6 mb-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
          </svg>
          <span className="text-xs font-semibold">Courses</span>
        </button>
        <button
          onClick={() => navigate("/worker/certificates")}
          className="flex flex-col items-center py-2 px-4 text-slate-500 hover:text-[#0a2240] transition-colors"
          data-testid="nav-certificates"
        >
          <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <span className="text-xs font-semibold">Certificates</span>
        </button>
        <button
          onClick={handleLogout}
          className="flex flex-col items-center py-2 px-4 text-slate-500 hover:text-red-500 transition-colors"
          data-testid="nav-logout"
        >
          <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="text-xs font-semibold">Logout</span>
        </button>
      </div>

      <div className="h-20"></div>
    </div>
  );
}
